// Navigation `state` for RR7 loaders.
//
// Legacy `history@3` kept `location.state` as an in-memory reference, so a page's
// `getInitialProps` could read whatever the previous page pushed: the filters and product
// that attribute a listing→detail hit in Segment, `videoPlay`/`videoTime`, `discardAd`,
// `lastFilter`… RR7 loaders get only `{request, params, context}` — `location.state` is not
// in `LoaderFunctionArgs` — and reading `window.history.state.usr` from a loader races with
// RR7's own history commit (the entry is committed *after* the loaders resolve).
//
// So the navigation helpers record the state here, keyed by target pathname, right before
// handing over to RR7, and the loaders read it back. Recording before `navigate` also keeps
// the values the native History API would reject: the shim strips React elements out of
// `state` to survive `structuredClone`, and the wizards push JSX inside their form data.
//
// Writes are client-only on purpose. A module-level cache on the SSR server would be shared
// across requests, and there is no state to recover there anyway — a cold document load had
// no previous page, in RR7 as in legacy. Loaders then see `{}`, which is what the pages
// already treat as "landed here directly".
const stateByPathname = new Map<string, Record<string, unknown>>()

// Writers push the URL they navigate to (sometimes with a query string), while loaders see a
// normalised pathname (several add the trailing slash `get_page_name_page_use_case` needs).
// Both sides go through this so the two always meet.
const toKey = (pathname: string): string => {
  const [path = ''] = pathname.split(/[?#]/)
  const withoutTrailingSlash = path.replace(/\/+$/, '')
  return withoutTrailingSlash === '' ? '/' : withoutTrailingSlash
}

export const setNavigationState = (pathname: string, state: unknown): void => {
  if (typeof window === 'undefined') return
  if (typeof state !== 'object' || state === null) return

  stateByPathname.set(toKey(pathname), state as Record<string, unknown>)
}

export const getNavigationState = (pathname: string): Record<string, unknown> => {
  if (typeof window === 'undefined') return {}

  return stateByPathname.get(toKey(pathname)) ?? {}
}
